/**
 * Pure conversion helpers for the super-admin money surfaces.
 *
 * The dashboard shows money in RM and rates in percent; Firestore stores
 * integer sen and 0..1 fractions. Everything that crosses that boundary lives
 * here, as pure functions with no React and no Firestore, because this is where
 * the expensive mistakes happen:
 *
 *   · `pctTo` rounds to a WHOLE percent. Applied to a payment-processing rate
 *     it would silently bill customers 3% instead of 2.5%; applied to a
 *     residual commission rate (ADR-0034) it would misprice a permanent
 *     obligation. That bug was caught in review, not by a test, because these
 *     helpers lived inside a 1300-line route component where nothing could
 *     reach them. Hence this module and its test file.
 *
 *   · A decimal comma is a 10x or 100x error, silently. `"2,5"` meaning 2.5%
 *     used to strip to `"25"` and store 25%. `"12,50"` meaning RM 12.50 used to
 *     strip to `"1250"` and store RM 1,250. Both parsed cleanly and both were
 *     wrong by two orders of magnitude on money. Commas are now accepted ONLY
 *     as thousands separators in a well-formed grouping, and rejected anywhere
 *     else rather than guessed at.
 *
 * Every parser rejects rather than coerces. There is no input this module will
 * silently reinterpret.
 */

/**
 * Strip whitespace and a trailing `%`, and normalise thousands separators.
 * Returns null when a comma is present in any position that is not a valid
 * `1,234,567` grouping — a decimal comma must never be mistaken for one.
 */
function normalise(raw: string): string | null {
  const s = raw.trim().replace(/[\s%]/g, '');
  if (!s.includes(',')) return s;
  // Accept only strict thousands grouping: 1,000 · 12,345.67 · 1,234,567
  if (!/^\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(s)) return null;
  return s.replace(/,/g, '');
}

// ── Sen ↔ RM ───────────────────────────────────────────────────────────────

/** Integer sen → display string in RM: 1234 → '12.34'. */
export function senToRm(sen: number): string {
  return (sen / 100).toFixed(2);
}

/** RM string → integer sen, or null when the input is not a clean RM amount. */
export function rmToSen(rm: string): number | null {
  const trimmed = normalise(rm);
  if (trimmed === null) return null;
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

/** As `rmToSen`, but throws a field-named error the form surfaces verbatim. */
export function rmToSenStrict(rm: string, fieldName: string): number {
  const v = rmToSen(rm);
  if (v === null) {
    throw new Error(`${fieldName} must be a non-negative RM amount — e.g. 12.50 or 1000.`);
  }
  return v;
}

// ── Percent ────────────────────────────────────────────────────────────────

/** 0..1 rate → display percent: 0.025 → '2.50'. */
export function pctFrom(rate: number): string {
  return (rate * 100).toFixed(2);
}

/** Parse a 0–100 percent with at most 2 decimals. Throws, never coerces. */
export function parsePct(pct: string, fieldName: string): number {
  const trimmed = normalise(pct);
  if (trimmed === null || !/^\d+(?:\.\d{1,2})?$/.test(trimmed)) {
    throw new Error(`${fieldName} must be a percentage with at most 2 decimals — e.g. 2.5.`);
  }
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0 || n > 100) {
    throw new Error(`${fieldName} must be between 0 and 100.`);
  }
  return n;
}

/**
 * Percent → 0..1 rate, ROUNDED TO A WHOLE PERCENT.
 *
 * Deliberately lossy, and used only for the first-invoice fee splits, where
 * whole percentages keep the four shares summing predictably. Never use this
 * for a rate where half a point is real money — see `pctToRate`.
 */
export function pctToWholeRate(pct: string, fieldName: string): number {
  return Math.round(parsePct(pct, fieldName)) / 100;
}

/**
 * Percent → 0..1 rate, keeping two decimals of PERCENT so 2.5 survives as
 * 0.025. Use this for anything where the gap between 2% and 2.5% is money:
 * residual commission rates (a permanent obligation, ADR-0034) and any
 * customer-facing fee rate.
 */
export function pctToRate(pct: string, fieldName: string): number {
  return Math.round(parsePct(pct, fieldName) * 100) / 10_000;
}

// ── Basis points ───────────────────────────────────────────────────────────

/** Basis points → display percent: 250 → '2.50'. */
export function pctFromBasisPoints(bp: number): string {
  return (bp / 100).toFixed(2);
}

/** Percent → basis points: '2.5' → 250. Integer, so no float reaches money maths. */
export function basisPointsFromPct(pct: string, fieldName: string): number {
  return Math.round(parsePct(pct, fieldName) * 100);
}

// ── Plain numbers ──────────────────────────────────────────────────────────

/** Non-negative number (tier multipliers, day counts). Throws, never coerces. */
export function numOrThrow(v: string, name: string): number {
  const trimmed = normalise(v);
  if (trimmed === null || !/^\d+(?:\.\d+)?$/.test(trimmed)) {
    throw new Error(`${name} must be a non-negative number.`);
  }
  const n = Number.parseFloat(trimmed);
  if (!Number.isFinite(n) || n < 0) throw new Error(`${name} must be a non-negative number.`);
  return n;
}

export function clampInt(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.floor(n)));
}
